"use client";

import { createContext, type ReactNode, useContext } from "react";
import type { BackendLang, BuildHeadersInput } from "./headers";

export type BackendCurrency = NonNullable<BuildHeadersInput["currency"]>;

/**
 * Carries backend i18n request headers owned by each app's active UI locale.
 * Amount-bearing endpoints that the backend converts per `currency` must key on
 * this value and send it, rather than falling back to the brand default.
 */
const BackendLangContext = createContext<BackendLang>("ru_RU");
const BackendCurrencyContext = createContext<BackendCurrency>("RUB");

export function BackendLangProvider({
  lang,
  children,
}: {
  lang: BackendLang;
  children: ReactNode;
}) {
  return <BackendLangContext.Provider value={lang}>{children}</BackendLangContext.Provider>;
}

export function BackendCurrencyProvider({
  currency,
  children,
}: {
  currency: BackendCurrency;
  children: ReactNode;
}) {
  return (
    <BackendCurrencyContext.Provider value={currency}>{children}</BackendCurrencyContext.Provider>
  );
}

/** Current backend `lang`; defaults to Russian when no provider is mounted. */
export function useBackendLang(): BackendLang {
  return useContext(BackendLangContext);
}

/** Current backend `currency`; defaults to RUB when no provider is mounted. */
export function useBackendCurrency(): BackendCurrency {
  return useContext(BackendCurrencyContext);
}
